import { Power } from 'lucide-react';
import { useState } from 'react';
import edgeService from '../../../services/edge.service';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface EdgeActionButtonsProps {
  edgeOnline: boolean;
  edgeId: string;
}

const EdgeActionButtons = ({ edgeOnline, edgeId }: EdgeActionButtonsProps) => {
  const [isRebooting, setIsRebooting] = useState(false);
  const [showRebootConfirm, setShowRebootConfirm] = useState(false);
  const toast = useToast();
  const { canUpdate } = usePermissions();

  const handleReboot = async () => {
    if (!edgeOnline) return;
    try {
      setIsRebooting(true);
      await edgeService.reboot(edgeId);
      toast.success('Reboot Initiated', 'The edge device is now rebooting. It will be offline for a few minutes.');
      setShowRebootConfirm(false);
    } catch (err: any) {
      toast.error('Reboot Failed', err.message || 'Failed to reboot edge');
    } finally {
      setIsRebooting(false);
    }
  };

  return (
    <>
      <div className="flex items-center space-x-3">
        {canUpdate && (
          <button
            onClick={() => setShowRebootConfirm(true)}
            disabled={!edgeOnline || isRebooting}
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              edgeOnline && !isRebooting
                ? 'bg-warning-600 text-white hover:bg-warning-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Power className={`w-4 h-4 ${isRebooting ? 'animate-pulse' : ''}`} />
            <span>{isRebooting ? 'Rebooting...' : 'Reboot Edge'}</span>
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={showRebootConfirm}
        onClose={() => setShowRebootConfirm(false)}
        onConfirm={handleReboot}
        title="Reboot Edge"
        message="Are you sure you want to reboot this edge? This will cause a service interruption."
        confirmText="Reboot"
        variant="danger"
        isLoading={isRebooting}
      />
    </>
  );
};

export default EdgeActionButtons;
